import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Spinner, Offcanvas } from "react-bootstrap";
import i18next from "../../i18n.js";
import { UserContext, hubEventTypes, urls, listTypes, customEventTypes, notificationTypes } from "../../constants.js";
import { eventDispatcher } from '../../eventDispatcher.js';
import { showPermissionRequest, loadListData } from '../helpers/HelperFunctions.js';
import { v4 } from 'uuid';
import DocumentHeader from '../documents/DocumentHeader.js';
import NotificationItem from './NotificationItem.js';

const NotificationBtnControl = () => {
    const [pushAllowed, setPushAllowed] = useState(false);
    const [count, setCount] = useState(0);
    const [infoLoaded, setInfoLoaded] = useState(false);
    const [notifications, setNotifications] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [show, setShow] = useState(false);

    const userContext = useContext(UserContext);
    const navigate = useNavigate();

    const notificationsRef = useRef(null);
    const dropdownRef = useRef(null);
    const listContanerRef = useRef(null);

    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications])

    useEffect(() => {
        fetch('/api/portalNotification/GetNotificationsInfo', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
            .then((response) => response.json())
            .then(result => {
                setPushAllowed(result.pushMessagesAllowed);
                setCount(result.notViewedCount);
                setInfoLoaded(true);
            });
        const onNotificationAddedId = eventDispatcher.subscrubeToEvent(hubEventTypes.notificationAdded, (notification) => {
            if (notificationsRef.current && !notificationsRef.current.some(n => n.id === notification.id)) {
                setNotifications((prevNotifications) => [notification, ...prevNotifications]);
            }
            setCount((prevCount) => prevCount + 1);
        });
        const onNotificationVievedId = eventDispatcher.subscrubeToEvent(hubEventTypes.notificationViewed, (notificationIds) => {
            if (notificationsRef.current) {
                setNotifications(prevNotifications => {
                    if (prevNotifications) {
                        const updatetdNots = structuredClone(prevNotifications);
                        const notifications = updatetdNots.filter(n => notificationIds.includes(n.id));
                        notifications.forEach(n => {
                            if (n.status === 'NEW') {
                                n.status = 'Viewed';
                            }
                        })
                        setCount(updatetdNots.filter(n => n.status === 'NEW'));
                        return updatetdNots;
                    }
                    return prevNotifications;
                });
            }
            else {
                setCount((prevCount) => prevCount > 0 ? prevCount - notificationIds.length : 0);
            }
        });

        if (userContext && userContext.token) {
            showPermissionRequest();
        }

        setInterval(() => saveViewedNotifications(), 5000);

        return () => {
            eventDispatcher.unsubscrubeFromEvent(onNotificationAddedId);
            eventDispatcher.unsubscrubeFromEvent(onNotificationVievedId);
            saveViewedNotifications();
        }
    }, []);

    useEffect(() => {
        if (loaded && userContext?.login) {
            const filter = [{
                name: 'RecipientUser',
                values: [userContext.login]
            }];
            loadListData(listTypes.Notifications, filter, 0, 1000, 'CreateDate', true).then(data => {
                if (data && data.items && data.items.length > 0) {
                    setNotifications(data.items.map(item => {
                        const title = item.fields.find(f => f.name === 'Title').value;
                        const message = item.fields.find(f => f.name === 'Message').value;
                        const id = item.fields.find(f => f.name === 'Id').value;
                        const status = item.fields.find(f => f.name === 'Status').value;
                        const content = item.fields.find(f => f.name === 'Content').value;
                        const type = item.fields.find(f => f.name === 'Type').value;
                        const created = item.fields.find(f => f.name === 'CreateDate').value;

                        const contentParts = content.split(';');
                        const docId = contentParts[0].split('/').pop();
                        const taskId = contentParts.length > 1 ? contentParts[1] : '';

                        return {
                            id,
                            title,
                            message,
                            status,
                            docId,
                            taskId,
                            type,
                            created
                        }
                    }));
                    setTimeout(() => viewNotifications(), 500);
                }
                else {
                    setNotifications([]);
                }
            });
        }
    }, [loaded])

    const getCounterClass = () => {
        if (count > 99) {
            return 'many-digits';
        }
        else if (count > 9) {
            return 'two-digits';
        }
        else {
            return 'one-digit';
        }
    }

    const savePushAllowedValue = (value) => {
        fetch(`/api/portalNotification/SetNotificationAllowed`, {
            method: 'Post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(value)
        })
    }

    const onCheckChanged = async (e) => {
        const checked = e.target.checked;
        if (checked != pushAllowed) {
            if (checked) {
                showPermissionRequest(true, () => {
                    savePushAllowedValue(true);
                    setPushAllowed(true);
                }, () => {
                    eventDispatcher.invokeEvent(customEventTypes.onShowMessage, {
                        id: v4(),
                        title: i18next.t('notifications.notificationsDeniedTitle'),
                        message: i18next.t('notifications.notificationsDeniedMessage'),
                        type: notificationTypes.rejected
                    });
                });
            }
            else {
                savePushAllowedValue(false);
                setPushAllowed(false);
            }
        }
    }

    const onNotificationClick = (id) => {
        const notification = notifications.find(n => n.id === id);
        if (notification) {
            if (notification.status === 'NEW') {
                fetch(`/api/portalNotification/ViewNotifications`, {
                    method: 'Post',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify([id])
                });
            }
            setShow(false);
            const url = notification.taskId ? `${urls.tasksPage}/${notification.taskId}?docId=${notification.docId}` : `${urls.documentsPage}/${notification.docId}`;
            navigate(url);
        }
    }

    const viewNotifications = () => {
        if (dropdownRef.current && listContanerRef.current && notificationsRef.current) {
            if (dropdownRef.current.classList.contains('show')) {
                const contanerRect = listContanerRef.current.getBoundingClientRect();

                const notViewedNots = notificationsRef.current.filter(n => n.status === 'NEW');
                if (notViewedNots.length > 0) {
                    const notViewedIds = [];
                    notViewedNots.forEach(n => {
                        const element = document.getElementById(n.id);
                        const elementRect = element.getBoundingClientRect();
                        if (elementRect.bottom < contanerRect.bottom + 15) {
                            notViewedIds.push(n.id);
                        }
                    });
                    if (notViewedIds.length > 0) {
                        setTimeout(() => {
                            setNotifications((prevNots => {
                                const updatedNots = structuredClone(prevNots);
                                const forUpdate = updatedNots.filter(n => notViewedIds.includes(n.id) && n.status === 'NEW');
                                forUpdate.forEach(n => {
                                    n.status = 'Viewing';
                                });
                                setCount(updatedNots.filter(n => n.status === 'NEW').length);
                                return updatedNots;
                            }));
                        }, 1000);
                    }
                }
            }
        }
    }

    const saveViewedNotifications = () => {
        if (notificationsRef.current) {
            const updatedNots = structuredClone(notificationsRef.current);

            const forUpdate = updatedNots.filter(n => n.status === 'Viewing');
            forUpdate.forEach(n => { n.status = 'Viewed' });
            const ids = forUpdate.map(n => n.id);
            if (ids.length > 0) {
                fetch(`/api/portalNotification/ViewNotifications`, {
                    method: 'Post',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(ids)
                });
                setNotifications(updatedNots);
            }
        }
    }

    const onToggleClick = () => {
        setLoaded(true);

        const mainContent = document.getElementsByClassName('main-content');
        if (mainContent.length > 0) {
            if (show) {
                mainContent[0].classList.remove('fixed');
            }
            else {
                mainContent[0].classList.add('fixed');
                mainContent[0].scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        }
        setShow(!show);
        setTimeout(() => viewNotifications(), 500);
    }

    return (
        <div className="notification-btn">
            <div className="notification-btn-toggle" onClick={onToggleClick} >
                {count > 0 && <div className={`counter ${getCounterClass()}`} >{count > 99 ? '99+' : count}</div>}
            </div>
            <div className={"notifications-block" + (show ? ' show' : '')} ref={dropdownRef} >
                <DocumentHeader title={i18next.t('notifications.notificationsListTitle')} handleBackClick={onToggleClick} />
                <div className='push-setings-block'>
                    <Form.Check
                        disabled={!infoLoaded}
                        type="switch"
                        id="push-setings"
                        label={i18next.t('notifications.pushSettingsLabel')}
                        checked={pushAllowed}
                        onChange={onCheckChanged}
                    />
                </div>
                {notifications === null && <Spinner animation="border" variant="primary" />}
                {notifications && notifications.length === 0 && <div className='no-notifications-label'>{i18next.t("notifications.noNotificationsLabel")}</div>}
                {notifications && notifications.length > 0 && <div className='notifications' ref={listContanerRef} onScroll={viewNotifications}>
                    {notifications.map(n => <NotificationItem notification={n} onNotificationClick={onNotificationClick} />)}
                </div>}
            </div>
            <Offcanvas show={show} onHide={onToggleClick} placement="end" className="canvas-nots" backdropClassName="canvas-nots-backdrop">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title><span className='title'>{i18next.t('notifications.notificationsListTitle')}</span></Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className='push-setings-block'>
                        <Form.Check
                            disabled={!infoLoaded}
                            type="switch"
                            id="push-setings"
                            label={i18next.t('notifications.pushSettingsLabel')}
                            checked={pushAllowed}
                            onChange={onCheckChanged}
                        />
                    </div>
                    {notifications === null && <Spinner animation="border" variant="primary" />}
                    {notifications && notifications.length === 0 && <div className='no-notifications-label'>{i18next.t("notifications.noNotificationsLabel")}</div>}
                    {notifications && notifications.length > 0 && <div className='notifications' ref={listContanerRef} onScroll={viewNotifications}>
                        {notifications.map(n => <NotificationItem notification={n} onNotificationClick={onNotificationClick} />)}
                    </div>}
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default NotificationBtnControl;
